import { Injectable } from '@angular/core';
import { FeaturesToggleSettingsService } from 'app/featuresToggleSettings/service/featuresToggleSettings.service';
import { catchError, filter, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { NavigationExtras, Router, UrlTree } from '@angular/router';
import { Utils } from '@shared/utils.service';
import { EupRoutesService } from '@core/eupRoutes.service';
import { ShellContextService } from '../shell-context/shell-context.service';
import { TimberService } from '@logging/timber.service';
import { GlobalSettings, GlobalSettingsService } from '@core/globalSettings.service';
import { PendoService } from '@shared/pendo/pendo.service';
import { RoleTypeEnum } from '@shared/enums';
import { CommunicationService } from 'app/services/client-communication/communication.service';
import { DownloadNotificationService } from '@shared/downloadNotification/downloadNotification.service';
import { AuthService, SessionInfo, UserCredentials } from './auth.service';
import { nonRedirectableRoutes } from '../../../assets/consts';
import { Consts } from '@shared/consts';
import { EupHttpHandler } from '@core/eupHttpHandler.service';
import { AuthProvider } from '@itero/auth-angular';
import { UserDataService } from './user-data.service';
import { isEnumValue } from '@shared/type-utils';

@Injectable({
	providedIn: 'root',
})
export class PostLoginService {
	get rolePath() {
		return this.globalSettings.rolePath();
	}

	constructor(
		private featuresToggleSettingsService: FeaturesToggleSettingsService,
		private utils: Utils,
		private eupRoutesService: EupRoutesService,
		private shellContextService: ShellContextService,
		private timberService: TimberService,
		private router: Router,
		private globalSettings: GlobalSettingsService,
		private pendoSerivce: PendoService,
		private communincationService: CommunicationService,
		private downloadNotificationService: DownloadNotificationService,
		private authService: AuthService,
		private eupHttpHandler: EupHttpHandler,
		private userDataService: UserDataService
	) {}

	init(sessionInfo: SessionInfo, region: string, provider: string | AuthProvider, email: string): Observable<UrlTree | boolean> {
		const { sessionId } = sessionInfo;

		this.shellContextService.updateIsAuth0(isEnumValue(provider, AuthProvider) && provider === AuthProvider.Auth0);
		this.timberService.info(`post process auth provider: ${provider} region: ${region} sessionId: ${sessionId}`, {
			module: 'PostLoginComponent',
		});

		if (isEnumValue(provider, AuthProvider) && provider === AuthProvider.Azure) {
			return this.handleAzureLogin(sessionInfo);
		} else {
			return this.login(sessionInfo, email);
		}
	}

	handleAzureLogin(sessionInfo: SessionInfo): Observable<UrlTree | boolean> {
		const { sessionId, accessToken } = sessionInfo;

		this.updateSessionInfo(sessionInfo);

		return this.eupHttpHandler
			.post(
				this.eupRoutesService.login.loginInternalToMidc,
				sessionId ? { sessionId } : {},
				{
					observe: 'response',
					headers: {
						Authorization: `Bearer ${accessToken}`,
					},
				},
				false,
				true
			)
			.pipe(
				tap(res => this.globalSettings.initialize({ ...res.body })),
				map(() => this.forwardToPage())
			);
	}

	login(sessionInfo: SessionInfo, email: string): Observable<UrlTree | boolean> {
		this.timberService.info('login called.', { module: 'PostLoginComponent' });

		return this.loginUser(sessionInfo, email).pipe(
			tap(() => {
				const authStatus = { isAuthenticated: true, userId: this.globalSettings.get().contactId };

				this.authService.authStatus$.next(authStatus);
				this.timberService.info(`authentication status: ${JSON.stringify(authStatus)}`, { module: 'PostLoginComponent' });
			}),
			catchError(err => {
				this.timberService.error('Error while trying to get login data.', { module: 'PostLoginComponent', error: err });

				return of(false);
			})
		);
	}

	loginUser(sessionInfo: SessionInfo, email: string): Observable<UrlTree | boolean> {
		this.timberService.info('loginUser called.', { module: 'PostLoginComponent' });

		return this.featuresToggleSettingsService.getFeaturesToggleSettings().pipe(
			switchMap(() => this.initSession(sessionInfo, email)),
			map(res => {
				if (res.shouldRedirect) {
					window.location.href = res.redirectUrl;

					return { redirect: true };
				}

				return res;
			}),
			filter((res: any) => !res.redirect),
			switchMap(res => {
				const loginStartingTime = new Date().getTime();

				this.timberService.sessionStartEvent(res.contactId, res.companies[0].id, res.roleType);

				return this.setUserData(res, email, loginStartingTime);
			}),
			catchError(err => {
				this.timberService.error('Error during loginUser process.', { module: 'PostLoginComponent', error: err });

				return of(false);
			})
		);
	}

	setUserData(settings: GlobalSettings, email: string, loginStartingTime: number): Observable<UrlTree | false> {
		this.timberService.info('setUserData called.', { module: 'PostLoginComponent' });

		if (settings.roleType === RoleTypeEnum.Internal) {
			this.globalSettings.initialize({ ...settings, roleType: RoleTypeEnum.Internal });

			return of(void 0);
		}
		settings.loginEmail = email;

		if ((settings as any).errorReasonText) {
			const errorMsg = (settings as any).errorReasonText;

			this.timberService.error(errorMsg, { module: 'PostLoginComponent' });

			return of(void 0);
		}

		if (settings.companies.length === 0) {
			this.timberService.error('No companies were found.', { module: 'PostLoginComponent' });

			return of(void 0);
		}

		this.downloadNotificationService.initialize();
		this.globalSettings.initialize(settings);

		let companyId = settings.companies[0].id;

		companyId = this.checkUrlForCompany(companyId);

		return this.globalSettings.setSelectedCompany(companyId).pipe(
			tap(() => {
				const region = settings.companies.find(c => c.id === companyId).region;

				this.communincationService.setRegion(region);
				this.shellContextService.updateContext(ctx => {
					ctx.bizCtx.region = region;
				});
			}),
			switchMap(() =>
				this.globalSettings.isDoctorSetReady$.pipe(
					filter(isReady => !!isReady),
					take(1)
				)
			),
			map(() => {
				if (settings.roleType === RoleTypeEnum.Lab) {
					const timeElapsed = new Date().getTime() - loginStartingTime;

					this.timberService.info(
						`Submit Login: Elapsed time from submit click to page navigation: ${timeElapsed / 1000} seconds. Logged in user ${settings.contactId} - Company Id ${settings.selectedCompanyId} - Role ${settings.roleType}`,
						{ module: 'PostLoginComponent' }
					);
				}

				return this.forwardToPage();
			}),
			tap(() => {
				try {
					if (settings && settings.selectedCompanyId) {
						this.pendoSerivce.initializePendo(settings.contactId, settings.selectedCompanyId);
					}
				} catch (e) {}
			})
		);
	}

	checkUrlForCompany(id: number): number {
		this.timberService.info('checkUrlForCompany called.', { module: 'PostLoginComponent' });

		const params = this.router.parseUrl(this.router.url.toLowerCase()).queryParams;
		let companyIdfromParams = 0;

		if (!!params.companyid) {
			companyIdfromParams = +params.companyid;
		} else if (!!params.returnurl) {
			const returnUrlparams = this.router.parseUrl(params.returnurl).queryParams;

			if (!!returnUrlparams.companyid) {
				companyIdfromParams = +returnUrlparams.companyid;
			}
		}

		const validCompanyIdUsed = this.globalSettings.get().companies.filter(x => x.id === companyIdfromParams).length === 1;

		return validCompanyIdUsed ? companyIdfromParams : id;
	}

	forwardToPage(): UrlTree | false {
		this.timberService.info('forwardToPage called.', { module: 'PostLoginComponent' });

		const params = this.router.parseUrl(this.router.url).queryParams;
		const navExtras: NavigationExtras = { queryParams: {} };

		if (params.companyid) {
			const urlParamIsValid = this.checkUrlForCompany(0) !== 0;

			if (urlParamIsValid) {
				navExtras.queryParams.companyid = params.companyid;
			}
		}

		let returnUrl = params.returnUrl || params.returnurl;

		if (returnUrl && this.isValidRedirectUrl(returnUrl)) {
			if (returnUrl.indexOf('/dist/') >= 0) {
				returnUrl = returnUrl.replace('/dist/', '/'); // fix for local redirection
			}

			const returnUrlParams = this.router.parseUrl(returnUrl).queryParams;

			Object.assign(navExtras.queryParams, returnUrlParams);

			const navigationUrl = returnUrl.split('?')[0];

			return this.router.createUrlTree([navigationUrl], navExtras);
		}

		// fallback — переход на home
		const fallbackUrl = `/${this.rolePath}/home`;

		return this.router.createUrlTree([fallbackUrl], navExtras);
	}

	isValidRedirectUrl(url: string): boolean {
		url = url.toLowerCase();

		const subRoutes = url.split('/');
		const lastSubRoute = subRoutes[subRoutes.length - 1];

		return !nonRedirectableRoutes.some(r => lastSubRoute.indexOf(r) >= 0);
	}

	initSession(sessionInfo: SessionInfo, email: string): Observable<GlobalSettings> {
		this.timberService.info('initSession called.', { module: 'PostLoginComponent' });

		const credentials: UserCredentials = { username: email, password: '' } as UserCredentials;

		return this.userDataService.proceedLoginFlow(sessionInfo, credentials).pipe(
			catchError(error => {
				this.timberService.error(`Error in auth0 login process for user ${this.safeHideEmail(email)}`, {
					module: 'PostLoginComponent',
					error,
				});
				localStorage.removeItem(Consts.Storage.Settings);

				return throwError(error);
			})
		);
	}

	safeHideEmail(email: string): string {
		try {
			return this.utils.hideEmail(email);
		} catch (error) {
			this.timberService.error('Error hiding email.', { module: 'PostLoginComponent', error });

			return '';
		}
	}

	updateSessionInfo(sessionInfo: SessionInfo): void {
		if (sessionInfo.sessionId) {
			this.shellContextService.updateContext(ctx => {
				ctx.session.sessionId = sessionInfo.sessionId;
				ctx.security.accessToken = sessionInfo.accessToken;
			});
		}
	}
}
